import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useAuth, type NexoraUser } from '../context/AuthContext';
import api from '../api/axios';
import { auth as authApi } from '../services/api';

interface LoginForm {
  email: string;
  password: string;
  societyCode?: string;
}

const ROLE_MAP: Record<string, NexoraUser['role']> = {
  superadmin:    'SUPER_ADMIN',
  super_admin:   'SUPER_ADMIN',
  society_admin: 'SOCIETY_ADMIN',
  societyadmin:  'SOCIETY_ADMIN',
  admin:         'SOCIETY_ADMIN',
  staff:         'STAFF',
  viewer:        'VIEWER',
  resident:      'RESIDENT',
  tenant:        'TENANT',
};

function normalizeRole(raw: string | null | undefined): NexoraUser['role'] {
  if (!raw) return undefined as unknown as NexoraUser['role'];
  return ROLE_MAP[raw.toLowerCase()] ?? (raw as NexoraUser['role']);
}

export function useLogin() {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (data: LoginForm) => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.post('/api/v1/auth/login', data);

      // Backend wraps response in { success, data: { ... } }
      const d: Record<string, unknown> = res.data?.data ?? res.data;

      const accessToken = d.accessToken as string;
      const refreshToken = d.refreshToken as string;

      const user: NexoraUser = {
        id:          (d.userId ?? d.id) as string,
        fullName:    d.fullName as string,
        email:       d.email as string,
        role:        normalizeRole(d.role as string),
        societyId:   (d.societyId as string) ?? null,
        societyName: (d.societyName as string) ?? null,
        lastLoginAt: (d.lastLoginAt as string) ?? null,
      };

      login(accessToken, refreshToken, user);

      // The login response is a TokenResponse, not the full profile — fields like
      // phone and societyName only come from /me. Backfill them in the background
      // so Settings shows them without needing another login.
      api.get('/api/v1/auth/me').then((meRes) => {
        const md: Record<string, unknown> = meRes.data?.data ?? meRes.data;
        updateUser({
          ...user,
          phone: (md.phone as string) ?? null,
          societyName: (md.societyName as string) ?? user.societyName,
          societyCode: (md.societyCode as string) ?? null,
        });
      }).catch(() => {});

      // SUPER_ADMIN always lands on /admin — never follow state.from which may
      // be a society-scoped route they have no access to.
      if (user.role === 'SUPER_ADMIN') {
        navigate('/admin', { replace: true });
      } else {
        const from = (location.state as { from?: Location })?.from;
        navigate(from?.pathname ?? '/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      const e = err as {
        code?: string;
        message?: string;
        response?: { status?: number; data?: { type?: string; message?: string; detail?: string; error?: { code?: string } } };
      };

      if (e.code === 'ECONNABORTED' || !e.response || /timeout/i.test(e.message ?? '') || /network/i.test(e.message ?? '')) {
        setError('The server is waking up from idle — this can take up to a minute on the free tier. Please try again in a few seconds.');
        return;
      }

      const msg = e.response?.data?.message ?? e.response?.data?.detail;
      // A 401 from the login endpoint is only a real credential failure when it's
      // our RFC7807 "invalid_auth" error. Any other 401 shape here (e.g. the generic
      // Spring Security entry point, which can transiently fire during a cold-start
      // filter-chain race) is a server hiccup, not proof the password was wrong.
      const isRealAuthFailure = e.response?.data?.type === 'urn:nexora:error:invalid_auth';
      if (e.response?.status === 401 && isRealAuthFailure) {
        // A specific backend message (e.g. "Society code is required...", "Account is
        // deactivated") is more useful than the generic access-request fallback below —
        // only fall back to that check when the backend gave the generic message.
        if (msg && msg !== 'Invalid credentials') {
          setError(msg);
          return;
        }
        const looksLikeEmail = data.email.includes('@');
        if (looksLikeEmail) {
          try {
            const status = await authApi.checkAccessRequest(data.email);
            if (status.pending) {
              setError("You've already submitted an access request. Please wait for your invite email.");
              return;
            }
          } catch {
            // endpoint unavailable — fall through to generic message
          }
        }
        setError('Invalid credentials. Please check your email/username and password.');
      } else if (e.response?.status === 401) {
        setError('The server is still starting up. Please try again in a few seconds.');
      } else {
        setError(msg ?? 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return { submit, isLoading, error };
}
